'use client';

import {
	ReactNode,
	createContext,
	useContext,
	useEffect,
	useRef,
	useCallback,
	useState,
} from 'react';
import {
	DlobWebsocketClient,
	MarketId,
	OrderbookGrouping,
	ResultSlotIncrementer,
} from '@velocity-exchange/common';
import useOrderbookDisplayStore, {
	getBestAvailableOrderbookSource,
} from 'src/stores/orderbook/useOrderbookDisplayStore';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { useSyncOrderbookDisplayState } from 'src/stores/orderbook/useSyncOrderbookDisplayState';
import { useWebsocketClient } from './hooks/useWebsocketClient';
import { useOrderbookDataPipeline } from './hooks/useOrderbookDataPipeline';
import { useOrderbookSubscription } from './hooks/useOrderbookSubscription';
import { useTabVisibilityAndRecovery } from './hooks/useTabVisibilityAndRecovery';

const OrderbookSubscriptionContext = createContext<boolean>(false);

export function useOrderbookSubscriptionContext(): boolean {
	return useContext(OrderbookSubscriptionContext);
}

export const OrderbookSubscriptionProvider = ({
	children,
}: {
	children?: ReactNode;
}) => {
	const selectedMarket = useVelocityStore((s) => s.selectedMarket);
	const setOrderbookStore = useOrderbookDisplayStore((s) => s.set);

	// All refs owned by the Provider
	const [subscriptionRestartKey, setSubscriptionRestartKey] = useState(0);
	const clientRef = useRef<DlobWebsocketClient | null>(null);
	const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
	const currentSubscriptionMarketIdRef = useRef<MarketId | null>(null);
	const currentSubscriptionGroupingRef = useRef<OrderbookGrouping | null>(null);
	const lastFallbackTimestamp = useRef<number>(0);
	const resultSlotIncrementer = useRef(new ResultSlotIncrementer());

	// Create websocket client
	const client = useWebsocketClient({
		clientRef,
		resultSlotIncrementer,
		lastFallbackTimestamp,
		subscriptionRestartKey,
	});

	// Create data pipeline
	const { createDataPipeline, destroyCurrentPipeline$ } =
		useOrderbookDataPipeline({
			client,
			lastFallbackTimestamp,
			subscriptionRestartKey,
		});

	/**
	 * Recovery function for websocket reconnection.
	 *
	 * FE-4066: This callback depends on destroyCurrentPipeline$, but is passed to
	 * useTabVisibilityAndRecovery as a stable callback. This prevents the tab visibility
	 * effect from directly depending on destroyCurrentPipeline$, which would cause a
	 * race condition during recovery (see useTabVisibilityAndRecovery JSDoc).
	 */
	const performRecovery = useCallback(() => {
		// 1. Signal to destroy the current pipeline
		destroyCurrentPipeline$.next();

		// 2. Unsubscribe and null the subscription
		if (subscriptionRef.current) {
			subscriptionRef.current.unsubscribe();
			subscriptionRef.current = null;
		}

		// 3. Clear subscription tracking refs
		currentSubscriptionMarketIdRef.current = null;
		currentSubscriptionGroupingRef.current = null;

		// 4. Destroy and null the client
		if (clientRef.current) {
			clientRef.current.destroy();
			clientRef.current = null;
		}

		// 5. Switch back to best available source
		setOrderbookStore((s) => {
			s.orderbookSource = getBestAvailableOrderbookSource();
		});

		// 6. Force recreation
		setSubscriptionRestartKey((prev) => prev + 1);
	}, [destroyCurrentPipeline$, setOrderbookStore]);

	/**
	 * Callback for tab inactive cleanup - signals pipeline destruction.
	 * Same pattern as performRecovery to avoid destroyCurrentPipeline$ dependency issue.
	 */
	const onTabInactiveCleanup = useCallback(() => {
		destroyCurrentPipeline$.next();
	}, [destroyCurrentPipeline$]);

	// Main subscription effect
	useOrderbookSubscription({
		client,
		subscriptionRef,
		currentSubscriptionMarketIdRef,
		currentSubscriptionGroupingRef,
		destroyCurrentPipeline$,
		createDataPipeline,
		subscriptionRestartKey,
	});

	// Tab visibility and recovery handling
	// FE-4066: destroyCurrentPipeline$ is not passed directly to this hook.
	// Instead, we pass performRecovery and onTabInactiveCleanup callbacks.
	// See useTabVisibilityAndRecovery JSDoc for details.
	useTabVisibilityAndRecovery({
		selectedMarketId: selectedMarket.marketId,
		clientRef,
		subscriptionRef,
		currentSubscriptionMarketIdRef,
		currentSubscriptionGroupingRef,
		lastFallbackTimestamp,
		performRecovery,
		onTabInactiveCleanup,
		setSubscriptionRestartKey,
	});

	// Listen for dev tool events to restart subscription
	useEffect(() => {
		if (
			typeof window !== 'undefined' &&
			(window as any).VelocityWindow?.devEventEmitter
		) {
			(window as any).VelocityWindow.devEventEmitter.on(
				'restart_orderbook_subscription',
				performRecovery
			);
		}

		return () => {
			if (
				typeof window !== 'undefined' &&
				(window as any).VelocityWindow?.devEventEmitter
			) {
				(window as any).VelocityWindow.devEventEmitter.off(
					'restart_orderbook_subscription',
					performRecovery
				);
			}
		};
	}, [performRecovery]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			// Signal to destroy all pipelines
			try {
				destroyCurrentPipeline$.next();
				destroyCurrentPipeline$.complete();
			} catch (error) {
				console.warn('Error completing destroyCurrentPipeline$:', error);
			}

			// Clean up subscription
			if (subscriptionRef.current) {
				try {
					subscriptionRef.current.unsubscribe();
					subscriptionRef.current = null;
				} catch (error) {
					console.warn('Error unsubscribing from orderbook stream:', error);
				}
			}

			// Clean up client
			if (clientRef.current) {
				try {
					clientRef.current.destroy();
					clientRef.current = null;
				} catch (error) {
					console.warn('Error destroying DLOB client:', error);
				}
			}

			// Reset refs to prevent memory leaks
			currentSubscriptionMarketIdRef.current = null;
			currentSubscriptionGroupingRef.current = null;

			// The client and destroyCurrentPipeline$ are created during render, so
			// tearing them down here is only correct when the component is really
			// going away. On a remount-in-place (StrictMode's simulated unmount in
			// dev) render does not re-run, so the next effect pass would reuse this
			// destroyed client — whose subscription pipeline is closed, making
			// `subscribeToMarkets` a silent no-op that never sends a subscribe frame
			// and drops the orderbook to polling. Bumping the key rebuilds both, the
			// same way the tab-return reconnect does. No-op on a real unmount.
			setSubscriptionRestartKey((prev) => prev + 1);
		};
	}, []);

	// Sync hooks
	useSyncOrderbookDisplayState();

	return (
		<OrderbookSubscriptionContext.Provider value={true}>
			{children}
		</OrderbookSubscriptionContext.Provider>
	);
};
